import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Upload, Download, Printer, Share2, Search } from "lucide-react";
import { toast } from "sonner";
import PdfPreview from "@/components/PdfPreview";

const CATEGORIES = { imza: "İmza Sirküleri", vergi: "Vergi Levhası", faaliyet: "Faaliyet Belgesi", sicil: "Sicil Gazetesi", kira: "Kira Sözleşmesi", diger: "Diğer" };
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx";

export default function CompanyDocuments({ canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [revision, setRevision] = useState(0);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("imza");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const input = useRef(null);
  const alive = useRef(true);
  const blobUrl = useRef(null);
  const uploadIntent = useRef(null);

  const release = () => {
    if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
    blobUrl.current = null;
  };
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; release(); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    api.get("/company-documents", { params: { offset, q: query } }).then(({ data }) => {
      if (!cancelled) { setItems(data.items); setTotal(data.total); }
    }).catch((e) => { if (!cancelled) setError(apiError(e.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset, query, revision]);

  const upload = async (event) => {
    event.preventDefault();
    if (busy || !file || !name.trim()) return;
    if (!file.size || file.size > 15 * 1024 * 1024) return toast.error("Belge boş olamaz; en fazla 15 MB olabilir.");
    setBusy(true);
    try {
      const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
      if (!alive.current) return;
      const fingerprint = JSON.stringify([name.trim(), category, file.name, Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")]);
      if (!uploadIntent.current || (uploadIntent.current.fingerprint !== null && uploadIntent.current.fingerprint !== fingerprint)) {
        uploadIntent.current = { id: crypto.randomUUID(), fingerprint };
      }
      const form = new FormData();
      form.append("file", file); form.append("name", name.trim()); form.append("category", category);
      form.append("request_id", uploadIntent.current.id);
      await api.post("/company-documents", form);
      if (!alive.current) return;
      setOffset(0); setQuery(""); setRevision((old) => old + 1); setName(""); setFile(null); input.current.value = "";
      uploadIntent.current = null;
      toast.success("Firma belgesi yüklendi.");
    } catch (e) { if (alive.current) toast.error(apiError(e.response?.data?.detail)); }
    finally { if (alive.current) setBusy(false); }
  };

  const view = async (document) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/company-documents/${document.id}/content`, { responseType: "blob" });
      if (!alive.current) return;
      release();
      const blob = new Blob([data], { type: document.content_type });
      blobUrl.current = URL.createObjectURL(blob);
      setSelected({ ...document, blob, url: blobUrl.current });
    } catch (_) { if (alive.current) toast.error("Belge açılamadı. Yetkinizi ve bağlantınızı kontrol edip tekrar deneyin."); }
    finally { if (alive.current) setBusy(false); }
  };

  const download = () => {
    const link = document.createElement("a");
    link.href = selected.url; link.download = selected.filename;
    document.body.appendChild(link); link.click(); link.remove();
  };
  const share = async () => {
    const attachment = new File([selected.blob], selected.filename, { type: selected.content_type });
    if (!navigator.canShare?.({ files: [attachment] }) || !navigator.share) {
      download();
      toast.info("Bu cihaz dosya paylaşımını desteklemiyor. İndirilen belgeyi e-posta veya WhatsApp mesajınıza ekleyin; otomatik gönderilmedi.");
      return;
    }
    try { await navigator.share({ files: [attachment], title: selected.name }); }
    catch (e) { if (e.name !== "AbortError") toast.error("Paylaşım tamamlanamadı. Belgeyi indirip ek olarak gönderebilirsiniz."); }
  };
  const previewable = selected && (selected.content_type === "application/pdf" || selected.content_type.startsWith("image/"));

  return <section className="space-y-4" data-testid="company-documents">
    <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-brand-green" /><div>
      <h2 className="font-heading text-lg font-bold">Firma Belgeleri</h2>
      <p className="text-sm text-slate-500">Bu firmaya ait genel evrakları saklayın, görüntüleyin, indirin ve paylaşın. Asıl dosya değiştirilmez.</p>
    </div></div>
    {canWrite && <form onSubmit={upload} className="rounded-lg border bg-white p-4 grid gap-3 md:grid-cols-2" data-testid="company-document-upload-form">
      <label className="text-sm font-medium">Belge adı<Input data-testid="company-document-name" value={name} disabled={busy} onChange={(e) => { uploadIntent.current = null; setName(e.target.value); }} maxLength={160} required placeholder="Örn. 2026 Vergi Levhası" /></label>
      <label className="text-sm font-medium">Belge türü<select className="mt-1 w-full rounded-md border p-2" value={category} disabled={busy} onChange={(e) => { uploadIntent.current = null; setCategory(e.target.value); }} data-testid="company-document-category">{Object.entries(CATEGORIES).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
      <label className="text-sm font-medium">Dosya (en fazla 15 MB)<input ref={input} type="file" required disabled={busy} accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" data-testid="company-document-file" /><span className="text-xs text-slate-500">PDF, JPG, PNG, WebP veya Word. İmzalı evrak için PDF ya da taranmış görsel kullanın.</span></label>
      <Button type="submit" className="self-end justify-self-start bg-brand-green text-white" disabled={busy || !file || !name.trim()} data-testid="company-document-upload"><Upload className="mr-2 h-4 w-4" />{busy ? "İşlem sürüyor…" : "Belge Yükle"}</Button>
    </form>}
    <label className="flex items-center gap-2 text-sm"><Search className="h-4 w-4" /><span className="sr-only">Belge ara</span><Input placeholder="Belge veya dosya adıyla ara" value={query} onChange={(e) => { setOffset(0); setQuery(e.target.value); }} maxLength={160} data-testid="company-document-search" /></label>
    {error ? <div role="alert" className="text-red-700">Belgeler yüklenemedi: {error} <Button variant="outline" onClick={() => setRevision((old) => old + 1)} data-testid="company-document-retry-list">Yeniden Dene</Button></div> : loading ? <p role="status">Belgeler yükleniyor…</p> : <div className="divide-y rounded-lg border bg-white">
      {items.map((item) => <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid="company-document-row">
        <div><p className="font-medium">{item.name}</p><p className="text-xs text-slate-500">{CATEGORIES[item.category]} · {item.filename} · {Math.ceil(item.size / 1024)} KB</p></div>
        {item.status === "ready" ? <Button variant="outline" onClick={() => view(item)} disabled={busy} data-testid={`company-document-open-${item.id}`}><FileText className="mr-2 h-4 w-4" />Görüntüle / İşlemler</Button> : <div className="text-sm text-amber-800">Yükleme tamamlanmadı.{canWrite && <Button variant="outline" disabled={busy} onClick={() => { uploadIntent.current = { id: item.id, fingerprint: null }; setName(item.name); setCategory(item.category); setFile(null); input.current.value = ""; input.current.focus(); toast.info("Aynı belge dosyasını yeniden seçip Belge Yükle'ye basın."); }} data-testid={`company-document-recover-${item.id}`}>Aynı Dosyayla Tamamla</Button>}</div>}
      </div>)}
      {!items.length && <p className="p-4 text-sm text-slate-500">{query ? "Aramanızla eşleşen belge yok." : "Henüz firma belgesi yüklenmemiş."}</p>}
    </div>}
    {!error && !loading && total > 25 && <div className="flex items-center gap-3 text-sm"><Button variant="outline" disabled={!offset} onClick={() => setOffset((old) => Math.max(0, old - 25))}>Önceki</Button><span>{offset + 1}–{Math.min(offset + 25, total)} / {total}</span><Button variant="outline" disabled={offset + 25 >= total} onClick={() => setOffset((old) => old + 25)}>Sonraki</Button></div>}
    <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); release(); } }}>
      <DialogContent className="max-w-4xl" style={{ maxHeight: "90vh", overflowY: "auto" }}><DialogHeader><DialogTitle>{selected?.name}</DialogTitle><DialogDescription>Belge bu firmaya ait özel arşivden alınır. Paylaşmadan önce alıcıyı kontrol edin.</DialogDescription></DialogHeader>
        {selected && <><div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild><a href={selected.url} download={selected.filename} data-testid="company-document-download"><Download className="mr-2 h-4 w-4" />İndir</a></Button>
          {previewable && <Button variant="outline" onClick={() => window.open(selected.url, "_blank", "noopener,noreferrer")} data-testid="company-document-print"><Printer className="mr-2 h-4 w-4" />Yazdırma Görünümü</Button>}
          <Button variant="outline" onClick={share} className="max-w-full h-auto min-h-10 whitespace-normal" data-testid="company-document-share"><Share2 className="mr-2 h-4 w-4" />Paylaş (e-posta / WhatsApp)</Button>
        </div>
        <p className="text-xs text-slate-500">Yazdırma görünümünde tarayıcının yazdır düğmesini veya Ctrl+P / ⌘P kullanın. Word belgelerini indirdikten sonra yazdırabilirsiniz.</p>
        {selected.content_type.startsWith("image/") ? <img src={selected.url} alt={selected.name} style={{ maxHeight: "60vh" }} className="object-contain" /> : selected.content_type === "application/pdf" ? <PdfPreview blob={selected.blob} name={selected.name} /> : <p>Bu dosya türü için tarayıcı önizlemesi yok; İndir düğmesini kullanın.</p>}
        </>}
      </DialogContent>
    </Dialog>
  </section>;
}
